import { z } from 'zod';

/**
 * F36 — Club (the B2B account subtype for academies/schools/sports-clubs).
 *
 * A club is a `b2b_accounts` row with `account_kind='club'` (D1). It reuses
 * F11's account + F13's staff (`b2b_account_members`) + audit + seat-limit
 * plumbing verbatim — F36 adds only the discriminator, the `club_admin` /
 * `club_trainer` roles (see b2b-member.ts), and the club-scoped surfaces below.
 *
 * Club settings = the existing editable b2b_accounts fields (no `description`
 * column at MVP — the migration adds none). `managedByClub` on a team is
 * derived (`teams.managed_by_b2b_account_id IS NOT NULL`), not stored.
 */

// 'organizer' (F41 — federations/leagues/event companies) was added to the
// b2b_accounts.account_kind DB CHECK but never to this type. Widened here so it
// matches the DB + the api ORGANIZER_CAPABLE_KINDS set; the admin actor-creation
// path (super-powers) is the first code that creates an organizer account.
export const ACCOUNT_KIND_VALUES = ['venue', 'trainer', 'club', 'organizer'] as const;
export type AccountKind = (typeof ACCOUNT_KIND_VALUES)[number];

/** Read shape for the club account (a b2b_accounts row, account_kind='club'). */
export type ClubDetail = {
  readonly id: string;
  readonly legalBusinessName: string;
  readonly displayName: string;
  readonly taxId: string | null;
  readonly countryCode: string;
  readonly status: 'active' | 'suspended' | 'disabled';
  readonly accountKind: AccountKind;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Derived counts for the club dashboard header. */
  readonly teamCount: number;
  readonly trainerCount: number;
  /**
   * F09 v2 — the club tick (`b2b_accounts.verification_status = 'verified'`).
   * Also drives the settings name-lock banner (E4).
   */
  readonly verified: boolean;
};

export type ClubSummary = {
  readonly id: string;
  readonly displayName: string;
  readonly status: ClubDetail['status'];
  readonly teamCount: number;
  readonly trainerCount: number;
  readonly countryCode: string;
  readonly createdAt: string;
  /** F09 v2 — the club tick. */
  readonly verified: boolean;
};

/**
 * One row of `GET /me/club-affiliations` (the player's coached teams under a
 * club), as produced by `apps/api/src/routes/me-club.ts`. F09 v2 adds
 * `clubVerified` (the club account's tick).
 */
export type PlayerClubAffiliationRow = {
  readonly teamId: string;
  readonly teamName: string;
  readonly sportName: string;
  readonly clubAccountId: string;
  readonly clubName: string;
  readonly clubVerified: boolean;
};

/** PATCH /me/club — owner/club_admin edits the club's identity fields. */
export const UpdateClubInputSchema = z
  .object({
    legalBusinessName: z.string().min(3).max(200).optional(),
    displayName: z.string().min(2).max(80).optional(),
    taxId: z.string().max(50).nullable().optional(),
  })
  .strict();
export type UpdateClubInput = z.infer<typeof UpdateClubInputSchema>;

/**
 * Every F36 club-surface error code. The route layer maps these to HTTP
 * statuses (403 for the NOT_* authZ codes, 409 for STALE_*, 422 for the rest).
 */
export const CLUB_ERROR_CODES = [
  'CLUB_NOT_FOUND',
  'NOT_A_CLUB_ACCOUNT',
  'NOT_CLUB_ADMIN',
  'NOT_CLUB_TRAINER',
  'NOT_ASSIGNED_TO_TEAM',
  'CLUB_TEAM_LIMIT_EXCEEDED',
  'CLUB_TRAINER_LIMIT_EXCEEDED',
  'TRAINER_ALREADY_ASSIGNED',
  'TRAINER_NOT_ASSIGNED',
  'TEAM_NOT_CLUB_MANAGED',
  'STALE_SCHEDULE',
  'STALE_TEAM',
  // FIX 5.23 (§7.7-10a) — a stale club settings write (it said STALE_TEAM).
  'STALE_CLUB',
  // FIX 5.23 — an unknown schedule (it said CLUB_NOT_FOUND).
  'SCHEDULE_NOT_FOUND',
  // FIX 5.23 — no active user has the roster email (it said CLUB_NOT_FOUND).
  'USER_NOT_FOUND',
  // FIX 5.2 — a suspended club is read-only (F36 D11).
  'CLUB_SUSPENDED',
  // FIX 5.2 — a team trainer must be a club member; a schedule's trainer must
  // be assigned to the team.
  'TRAINER_NOT_CLUB_MEMBER',
  'TRAINER_NOT_ON_TEAM',
  'SCHEDULE_CONFLICT',
  'ATTENDANCE_WINDOW_CLOSED',
  'ATTENDANCE_PLAYER_NOT_ON_ROSTER',
  'FRIENDLY_MATCH_NOT_FOUND',
  'FRIENDLY_ALREADY_DECIDED',
  'FRIENDLY_SAME_TEAM',
  'FRIENDLY_NOT_OPPONENT',
  'SESSION_NOT_TRAINING',
  'VALIDATION_ERROR',
] as const;
export type ClubErrorCode = (typeof CLUB_ERROR_CODES)[number];
